import React from "react";
import AdminPanel from "../shared/AdminPanel";
import PageState from "../shared/PageState";
import panelStyles from "../styles/Panel.module.css";
import analyticsStyles from "../styles/Analytics.module.css";
import {
  EMPTY_ANALYTICS,
  EMPTY_FUNNEL_PERIOD,
  EMPTY_MONEY_PERIOD
} from "../constants";
import { formatDateTime } from "../utils/formatters";
import AnalyticsFunnelBlock from "./analytics/AnalyticsFunnelBlock";
import AnalyticsDropoffBlock from "./analytics/AnalyticsDropoffBlock";
import AnalyticsMoneyBlock from "./analytics/AnalyticsMoneyBlock";
import AnalyticsPurchaseSeriesBlock from "./analytics/AnalyticsPurchaseSeriesBlock";
import AnalyticsProductsBlock from "./analytics/AnalyticsProductsBlock";
import AnalyticsRetentionBlock from "./analytics/AnalyticsRetentionBlock";
import AnalyticsWeeklyBlock from "./analytics/AnalyticsWeeklyBlock";
import AnalyticsCohortsBlock from "./analytics/AnalyticsCohortsBlock";
import AnalyticsRolesBlock from "./analytics/AnalyticsRolesBlock";
import AnalyticsSummaryBlock from "./analytics/AnalyticsSummaryBlock";
import AnalyticsHelpBlock from "./analytics/AnalyticsHelpBlock";
import AnalyticsRequestsBlock from "./analytics/AnalyticsRequestsBlock";

function AnalyticsContent({ analyticsData, analyticsLoading, tab }) {
  const periodDay = analyticsData?.periods?.day || EMPTY_FUNNEL_PERIOD;
  const periodWeek = analyticsData?.periods?.week || EMPTY_FUNNEL_PERIOD;
  const periodMonth = analyticsData?.periods?.month || EMPTY_FUNNEL_PERIOD;
  const moneyDay = analyticsData?.money?.day || EMPTY_MONEY_PERIOD;
  const moneyWeek = analyticsData?.money?.week || EMPTY_MONEY_PERIOD;
  const moneyMonth = analyticsData?.money?.month || EMPTY_MONEY_PERIOD;
  const activeByPlan = analyticsData?.money?.activeByPlan || EMPTY_ANALYTICS.money.activeByPlan;
  const retention = analyticsData?.retention || EMPTY_ANALYTICS.retention;
  const dropOff = analyticsData?.dropOff || EMPTY_ANALYTICS.dropOff;
  const cohorts = analyticsData?.cohorts || EMPTY_ANALYTICS.cohorts;
  const roles = analyticsData?.roles || EMPTY_ANALYTICS.roles;
  const weekly = analyticsData?.weekly || EMPTY_ANALYTICS.weekly;
  const requests = analyticsData?.requests || EMPTY_ANALYTICS.requests;
  const conclusions = analyticsData?.conclusions || EMPTY_ANALYTICS.conclusions;
  const dashboard = analyticsData?.dashboard || EMPTY_ANALYTICS.dashboard;

  if (analyticsData.error) {
    return (
      <PageState
        tone="error"
        title="Не удалось загрузить аналитику"
        description={analyticsData.error.message || "Неизвестная ошибка аналитики."}
      />
    );
  }

  if (analyticsLoading && !analyticsData.generatedAt && !analyticsData.updatedAt) {
    return <PageState title="Загружаем аналитику" description="Получаем актуальные данные дашборда." />;
  }

  return (
    <>
      <div className={analyticsStyles.meta}>
        <span>Обновлено: {formatDateTime(analyticsData.updatedAt || analyticsData.generatedAt)}</span>
        {analyticsData.refreshing ? <span className={analyticsStyles.chip}>Обновляется...</span> : null}
        {analyticsData.stale ? <span className={analyticsStyles.chipWarn}>Данные устарели</span> : null}
      </div>

      <AnalyticsHelpBlock />

      {tab === "overview" ? (
        <>
          <AnalyticsSummaryBlock analyticsData={analyticsData} conclusions={conclusions} dashboard={dashboard} />
          <AnalyticsWeeklyBlock weekly={weekly} />
          <AnalyticsPurchaseSeriesBlock purchaseSeries={weekly.purchaseSeries} />
        </>
      ) : null}

      {tab === "funnel" ? (
        <>
          <AnalyticsFunnelBlock day={periodDay} week={periodWeek} month={periodMonth} />
          <AnalyticsDropoffBlock dropOff={dropOff} />
        </>
      ) : null}

      {tab === "retention" ? (
        <>
          <AnalyticsRetentionBlock analyticsData={analyticsData} retention={retention} />
          <AnalyticsCohortsBlock cohorts={cohorts} />
        </>
      ) : null}

      {tab === "revenue" ? (
        <>
          <AnalyticsMoneyBlock
            analyticsData={analyticsData}
            activeByPlan={activeByPlan}
            day={moneyDay}
            week={moneyWeek}
            month={moneyMonth}
          />
          <AnalyticsProductsBlock day={moneyDay} week={moneyWeek} month={moneyMonth} />
          <AnalyticsPurchaseSeriesBlock purchaseSeries={weekly.purchaseSeries} />
        </>
      ) : null}

      {tab === "cohorts" ? <AnalyticsCohortsBlock cohorts={cohorts} /> : null}
      {tab === "roles" ? <AnalyticsRolesBlock roles={roles} /> : null}
      {tab === "requests" ? <AnalyticsRequestsBlock requests={requests} /> : null}
    </>
  );
}

export function AnalyticsSection({
  mode = "panel",
  tab = "overview",
  isOpen = true,
  onToggle,
  analyticsData,
  analyticsLoading,
  onRefresh
}) {
  if (mode === "page") {
    return <AnalyticsContent analyticsData={analyticsData} analyticsLoading={analyticsLoading} tab={tab} />;
  }

  return (
    <AdminPanel
      id="analytics"
      title="Аналитика"
      isOpen={isOpen}
      onToggle={onToggle}
      actions={
        <button type="button" className={panelStyles.button} onClick={onRefresh} disabled={analyticsLoading}>
          {analyticsLoading ? "Обновляем..." : "Обновить"}
        </button>
      }
    >
      <AnalyticsContent analyticsData={analyticsData} analyticsLoading={analyticsLoading} tab="overview" />
      <AnalyticsFunnelBlock
        day={analyticsData?.periods?.day || EMPTY_FUNNEL_PERIOD}
        week={analyticsData?.periods?.week || EMPTY_FUNNEL_PERIOD}
        month={analyticsData?.periods?.month || EMPTY_FUNNEL_PERIOD}
      />
      <AnalyticsDropoffBlock dropOff={analyticsData?.dropOff || EMPTY_ANALYTICS.dropOff} />
      <AnalyticsMoneyBlock
        analyticsData={analyticsData}
        activeByPlan={analyticsData?.money?.activeByPlan || EMPTY_ANALYTICS.money.activeByPlan}
        day={analyticsData?.money?.day || EMPTY_MONEY_PERIOD}
        week={analyticsData?.money?.week || EMPTY_MONEY_PERIOD}
        month={analyticsData?.money?.month || EMPTY_MONEY_PERIOD}
      />
      <AnalyticsProductsBlock
        day={analyticsData?.money?.day || EMPTY_MONEY_PERIOD}
        week={analyticsData?.money?.week || EMPTY_MONEY_PERIOD}
        month={analyticsData?.money?.month || EMPTY_MONEY_PERIOD}
      />
      <AnalyticsPurchaseSeriesBlock purchaseSeries={analyticsData?.weekly?.purchaseSeries || EMPTY_ANALYTICS.weekly.purchaseSeries} />
      <AnalyticsRetentionBlock
        analyticsData={analyticsData}
        retention={analyticsData?.retention || EMPTY_ANALYTICS.retention}
      />
      <AnalyticsCohortsBlock cohorts={analyticsData?.cohorts || EMPTY_ANALYTICS.cohorts} />
      <AnalyticsRolesBlock roles={analyticsData?.roles || EMPTY_ANALYTICS.roles} />
    </AdminPanel>
  );
}

export default AnalyticsSection;
